import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, formatGBP } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_WHITE_URL } from "@/components/Logo";
import { ImageUpload } from "@/components/ImageUpload";
import { MultiImageUpload } from "@/components/MultiImageUpload";
import { HoverMediaUpload } from "@/components/HoverMediaUpload";
import { SIZES, COLOUR_PALETTE, colourHex, isLightColour } from "@/lib/productOptions";
import { PRODUCT_BADGE_OPTIONS } from "@/components/ProductBadges";
import { toast } from "sonner";
import {
  Package, Tag, Percent, Mail, ShoppingBag, Settings as Cog, LogOut, ExternalLink,
  Plus, Trash2, Pencil, Send, X, Check, Users, ShieldCheck,
  Clock, Loader2, Truck, Navigation, PackageCheck, CheckCircle2, XCircle, CreditCard, Cookie, Eye, Scissors, Download, Menu, Store, Barcode,
} from "lucide-react";

// Must match the `categories` table (supabase/migrations/*_seed_jewellery.sql).
// "New In" and "Sale" are product flags, not categories.
const CATEGORIES = ["necklace-sets", "bridal-sets", "bangles", "earrings", "maang-tikka", "rings",
  "nose-rings", "anklets", "hair-accessories"];
const TABS = [
  { id: "products", label: "Products", icon: Package },
  { id: "offers", label: "Offers", icon: Percent },
  { id: "sale", label: "Sale Countdown", icon: Clock },
  { id: "discounts", label: "Discount Codes", icon: Tag },
  { id: "customers", label: "Customers", icon: Users },
  { id: "subscribers", label: "Subscribers", icon: Mail },
  { id: "orders", label: "Orders", icon: ShoppingBag },
  { id: "cookies", label: "Cookie Consents", icon: Cookie },
  { id: "settings", label: "Settings", icon: Cog },
];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-wide text-gray-500">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
const inputCls = "w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-pink";
const btnPink = "bg-pink text-white px-4 py-2 text-xs uppercase tracking-[0.12em] hover:bg-pink-hover transition-colors";
const btnDark = "bg-charcoal text-white px-4 py-2 text-xs uppercase tracking-[0.12em] hover:bg-pink transition-colors";
const btnGhost = "border border-charcoal/20 text-charcoal px-4 py-2 text-xs uppercase tracking-[0.12em] hover:bg-charcoal hover:text-white transition-colors";

// ---------------- Products ----------------
const emptyProduct = {
  name: "", subtitle: "", category: "necklace-sets", price: 0, original_price: "",
  image: "", hover_image: "", hover_video: "", images: [], description: "", colour_stock: [], variant_stock: [], sizes: [], stock: 0,
  featured: false, on_sale: false, new_in: false, active: true,
  badges: [], basket_count: 0, alterable: false, alteration_allowance: "", alteration_direction: "both", alteration_up: "", alteration_down: "",
};

const seedVariantStock = (colourStock, sizes) => {
  const cs = colourStock || [];
  const sz = sizes || [];
  if (cs.length && sz.length) return cs.flatMap((c) => sz.map((s) => ({ colour: c.colour, size: s, stock: c.stock || 0 })));
  if (cs.length) return cs.map((c) => ({ colour: c.colour, size: null, stock: c.stock || 0 }));
  if (sz.length) return sz.map((s) => ({ colour: null, size: s, stock: 0 }));
  return [];
};

function Products({ api }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  // One product list is shared with the inventory; here only the pieces on zebaa.uk. Shop-only items are
  // added in the inventory, which is also where website colours/sizes get their SKU & barcode.
  const live = items.filter((p) => p.active !== false);
  const [customSize, setCustomSize] = useState("");
  const [measure, setMeasure] = useState({ bust: "", waist: "", hips: "" });
  const [discInput, setDiscInput] = useState("");
  const calcDisc = (p) => {
    const op = parseFloat(p?.original_price);
    const pr = parseFloat(p?.price);
    if (op > 0 && pr >= 0 && op >= pr) return String(Math.round((1 - pr / op) * 100));
    return "";
  };

  const load = useCallback(() => {
    api().get("/admin/products").then(({ data }) => setItems(data)).catch(() => toast.error("Failed to load products"));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  // Lock background scroll while the product editor modal is open (prevents
  // mobile scroll-chaining that hijacks the page behind the modal).
  useEffect(() => {
    if (!editing) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [editing]);

  const openNew = () => { setDiscInput(""); setEditing({ ...emptyProduct, colour_stock: [], sizes: [] }); };
  const importRef = useRef(null);
  const exportCatalog = async () => {
    try {
      const { data } = await api().get("/admin/export-catalog");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `zebaa-catalog-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.count} products`);
    } catch (e) { toast.error("Export failed"); }
  };
  const importCatalog = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm("Import products from this file? This REPLACES all current products on this site.")) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const products = parsed.products || parsed;
      const { data } = await api().post("/admin/import-catalog", { products, replace: true });
      toast.success(`Imported ${data.imported} products`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed — check the file."); }
  };
  const openEdit = (p) => {
    const colourStock = (p.colour_stock && p.colour_stock.length)
      ? p.colour_stock
      : (p.colors || []).map((c) => ({ colour: c, stock: 0 }));
    const sizes = orderSizes(p.sizes || []);
    setDiscInput(calcDisc(p));
    setEditing({
      ...p, original_price: p.original_price ?? "", category: p.category || "necklace-sets",
      sizes,
      hover_image: p.hover_image || "",
      hover_video: p.hover_video || "",
      images: (p.images && p.images.length) ? p.images : [p.image].filter(Boolean),
      badges: p.badges || [],
      basket_count: p.basket_count || 0,
      alterable: p.alterable || false,
      alteration_allowance: p.alteration_allowance || "",
      alteration_direction: p.alteration_direction || "both",
      alteration_up: (p.alteration_up ?? (p.alteration_direction === "smaller" ? 0 : p.alteration_allowance)) || "",
      alteration_down: (p.alteration_down ?? (p.alteration_direction === "larger" ? 0 : p.alteration_allowance)) || "",
      colour_stock: colourStock,
      variant_stock: (p.variant_stock && p.variant_stock.length)
        ? p.variant_stock
        : seedVariantStock(colourStock, sizes),
      tailoring_allowance: p.tailoring_allowance || "",
    });
  };

  const sameCell = (x, colour, size) => (x.colour ?? "") === (colour ?? "") && (x.size ?? "") === (size ?? "");
  const getCell = (colour, size) => {
    const v = (editing.variant_stock || []).find((x) => sameCell(x, colour, size));
    return v ? v.stock : "";
  };
  const setCell = (colour, size, val) => {
    // Store the raw input so the field can be cleared/backspaced freely; coerce at save.
    const list = (editing.variant_stock || []).filter((x) => !sameCell(x, colour, size));
    list.push({ colour: colour || null, size: size || null, stock: val });
    setEditing({ ...editing, variant_stock: list });
  };

  const hasColours = editing && editing.colour_stock && editing.colour_stock.length > 0;
  const hasSizes = editing && (editing.sizes || []).length > 0;
  const matrixTotal = () => {
    const cols = (editing?.colour_stock || []).map((c) => c.colour);
    const sz = editing?.sizes || [];
    const n = (c, s) => Number(getCell(c, s)) || 0;
    if (cols.length && sz.length) return cols.reduce((t, col) => t + sz.reduce((s, z) => s + n(col, z), 0), 0);
    if (cols.length) return cols.reduce((t, col) => t + n(col, null), 0);
    if (sz.length) return sz.reduce((t, z) => t + n(null, z), 0);
    return parseInt(editing?.stock) || 0;
  };
  const toggleColour = (name) => {
    const cs = editing.colour_stock || [];
    const exists = cs.find((c) => c.colour === name);
    if (exists) {
      // Removing a colour also drops its per-colour images and stock cells so its
      // photos disappear from the cover grid / gallery immediately.
      setEditing({
        ...editing,
        colour_stock: cs.filter((c) => c.colour !== name),
        colour_images: (editing.colour_images || []).filter((c) => c.colour !== name),
        variant_stock: (editing.variant_stock || []).filter((x) => (x.colour ?? "") !== name),
      });
    } else {
      setEditing({ ...editing, colour_stock: [...cs, { colour: name, stock: 0 }] });
    }
  };
  const setColourStock = (name, val) => {
    setEditing({
      ...editing,
      colour_stock: editing.colour_stock.map((c) => c.colour === name ? { ...c, stock: parseInt(val) || 0 } : c),
    });
  };
  const setColourImages = (name, imgs) => {
    const ci = (editing.colour_images || []).filter((c) => c.colour !== name);
    setEditing({ ...editing, colour_images: [...ci, { colour: name, images: imgs }] });
  };
  const colourImagesFor = (name) => (editing.colour_images || []).find((c) => c.colour === name)?.images || [];
  // Keep sizes in a consistent order: standard letter sizes first (S→XXL), custom labels after.
  const orderSizes = (arr) => {
    const std = SIZES.filter((s) => arr.includes(s));
    const custom = arr.filter((s) => !SIZES.includes(s));
    return [...std, ...custom];
  };
  const toggleSize = (s) => {
    const sizes = editing.sizes || [];
    const next = sizes.includes(s) ? sizes.filter((x) => x !== s) : [...sizes, s];
    setEditing({ ...editing, sizes: orderSizes(next) });
  };
  const addCustomSize = () => {
    const label = customSize.trim();
    if (!label) return;
    const sizes = editing.sizes || [];
    if (!sizes.includes(label)) setEditing({ ...editing, sizes: orderSizes([...sizes, label]) });
    setCustomSize("");
  };
  const removeSize = (s) => {
    setEditing({ ...editing, sizes: (editing.sizes || []).filter((x) => x !== s) });
  };
  const addMeasurementSize = () => {
    const parts = [];
    if (String(measure.bust).trim()) parts.push(`Bust ${String(measure.bust).trim()}"`);
    if (String(measure.waist).trim()) parts.push(`Waist ${String(measure.waist).trim()}"`);
    if (String(measure.hips).trim()) parts.push(`Hip ${String(measure.hips).trim()}"`);
    if (!parts.length) return;
    const label = parts.join(" · ");
    const sizes = editing.sizes || [];
    if (!sizes.includes(label)) setEditing({ ...editing, sizes: orderSizes([...sizes, label]) });
    setMeasure({ bust: "", waist: "", hips: "" });
  };

  const save = async () => {
    if (!(parseFloat(editing.price) > 0)) { toast.error("Add the website price."); return; }
    const colours = (editing.colour_stock || []).map((c) => c.colour);
    const sizes = editing.sizes || [];
    const nCell = (c, s) => Number(getCell(c, s)) || 0;
    // Build the variant_stock matrix from the currently selected colours & sizes.
    let variant_stock = [];
    if (colours.length && sizes.length) {
      variant_stock = colours.flatMap((col) => sizes.map((s) => ({ colour: col, size: s, stock: nCell(col, s) })));
    } else if (colours.length) {
      variant_stock = colours.map((col) => ({ colour: col, size: null, stock: nCell(col, null) }));
    } else if (sizes.length) {
      variant_stock = sizes.map((s) => ({ colour: null, size: s, stock: nCell(null, s) }));
    }
    // colour_stock kept as per-colour totals (for swatches & legacy fallbacks).
    const colour_stock = colours.map((col) => ({
      colour: col,
      stock: sizes.length
        ? sizes.reduce((sum, s) => sum + nCell(col, s), 0)
        : nCell(col, null),
    }));
    const totalStock = variant_stock.length
      ? variant_stock.reduce((sum, v) => sum + (v.stock || 0), 0)
      : parseInt(editing.stock) || 0;
    // keep only colour_images for currently-selected colours, drop empties
    const colour_images = (editing.colour_images || [])
      .filter((c) => colours.includes(c.colour) && (c.images || []).filter(Boolean).length)
      .map((c) => ({ colour: c.colour, images: c.images.filter(Boolean) }));
    const body = {
      ...editing,
      price: parseFloat(editing.price) || 0,
      original_price: editing.original_price === "" ? null : parseFloat(editing.original_price),
      // On sale is derived automatically: a product is "on sale" whenever it has an
      // original price higher than its current price (i.e. it's discounted).
      on_sale: editing.original_price !== "" && parseFloat(editing.original_price) > (parseFloat(editing.price) || 0),
      stock: totalStock,
      colour_stock,
      variant_stock,
      colour_images,
      colors: colours,
      sizes,
      badges: editing.badges || [],
      basket_count: parseInt(editing.basket_count) || 0,
      alterable: !!editing.alterable,
      alteration_direction: editing.alterable ? (editing.alteration_direction || "both") : "both",
      alteration_up: (() => {
        if (!editing.alterable) return 0;
        const dir = editing.alteration_direction || "both";
        return dir === "smaller" ? 0 : (Number(editing.alteration_up) || 0);
      })(),
      alteration_down: (() => {
        if (!editing.alterable) return 0;
        const dir = editing.alteration_direction || "both";
        return dir === "larger" ? 0 : (Number(editing.alteration_down) || 0);
      })(),
      alteration_allowance: (() => {
        if (!editing.alterable) return 0;
        const dir = editing.alteration_direction || "both";
        const up = dir === "smaller" ? 0 : (Number(editing.alteration_up) || 0);
        const down = dir === "larger" ? 0 : (Number(editing.alteration_down) || 0);
        return Math.max(up, down); // legacy field kept in sync
      })(),
      images: (() => {
        const flat = colour_images.flatMap((c) => c.images);
        if (flat.length) return [...new Set(flat)];
        return (editing.images && editing.images.length) ? editing.images.filter(Boolean) : [editing.image].filter(Boolean);
      })(),
      image: (() => {
        const flat = colour_images.flatMap((c) => c.images);
        if (editing.image && flat.includes(editing.image)) return editing.image;
        return flat[0] || (editing.images && editing.images[0]) || editing.image || "";
      })(),
      hover_image: editing.hover_image || "",
      hover_video: editing.hover_video || "",
    };
    delete body.id; delete body.slug; delete body._id; delete body.fabric;
    try {
      if (editing.id) await api().put(`/admin/products/${editing.id}`, body);
      else await api().post("/admin/products", body);
      toast.success("Product saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  // Products are shared with the inventory, so "delete" here only takes the piece off the website:
  // it stays in the shop stock with its SKUs and barcodes (delete it completely from the inventory).
  const del = async (id) => {
    if (!window.confirm("Take this off the website? It stays in your shop stock in the inventory, and you can link it again later.")) return;
    try { await api().post(`/admin/products/${id}/unpublish`); toast.success("Taken off the website"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't take it off the website"); }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <h2 className="font-serif text-3xl">Products <span className="text-gray-400 text-lg">({live.length})</span></h2>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={importRef} type="file" accept="application/json" className="hidden" onChange={importCatalog} data-testid="admin-import-input" />
          <button onClick={exportCatalog} className={btnGhost + " flex items-center gap-2"} data-testid="admin-export-catalog"><Download className="w-4 h-4" /> Export</button>
          <button onClick={() => importRef.current?.click()} className={btnGhost + " flex items-center gap-2"} data-testid="admin-import-catalog"><Download className="w-4 h-4 rotate-180" /> Import</button>
          <button onClick={openNew} className={btnPink + " flex items-center gap-2"} data-testid="admin-add-product"><Plus className="w-4 h-4" /> Add Product</button>
        </div>
      </div>

      <div className="hidden sm:block bg-white border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-surface text-left text-xs uppercase tracking-wide text-gray-500">
            <th className="px-4 py-3">Product</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Price</th><th className="px-4 py-3">Stock</th><th className="px-4 py-3">Flags</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {live.map((p) => (
              <tr key={p.id} className="border-t border-gray-100">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {p.image && <img src={p.image} alt="" className="w-10 h-12 object-cover bg-surface shrink-0" />}
                    <div><div className="font-medium">{p.name}</div><div className="text-xs text-gray-400">{p.subtitle}</div></div>
                  </div>
                </td>
                <td className="px-4 py-3 capitalize">{(p.category || "").replace("-", " ")}</td>
                <td className="px-4 py-3 tabular-nums">{formatGBP(p.price)}</td>
                <td className="px-4 py-3 tabular-nums">{p.stock ?? 0}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{[p.featured && "Featured", p.on_sale && "Sale", p.new_in && "New", ].filter(Boolean).join(", ")}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(p)} className="text-gray-500 hover:text-pink mr-3" data-testid={`edit-product-${p.slug}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(p.id)} className="text-gray-500 hover:text-pink" data-testid={`delete-product-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* mobile card list */}
      <div className="sm:hidden space-y-3" data-testid="admin-products-mobile">
        {live.map((p) => {
          const flags = [p.featured && "Featured", p.on_sale && "Sale", p.new_in && "New"].filter(Boolean);
          return (
            <div key={p.id} className="bg-white border border-gray-100 rounded-lg p-3 flex gap-3">
              {p.image
                ? <img src={p.image} alt="" className="w-16 h-20 object-cover bg-surface rounded shrink-0" />
                : <div className="w-16 h-20 bg-surface rounded shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-charcoal truncate">{p.name}</div>
                {p.subtitle && <div className="text-xs text-gray-400 truncate">{p.subtitle}</div>}
                <div className="text-xs text-gray-500 mt-1 capitalize">{p.category.replace("-", " ")} · {formatGBP(p.price)}</div>
                <div className="text-xs text-gray-500">Stock: {p.stock ?? 0}</div>
                {flags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {flags.map((f) => (
                      <span key={f} className="text-[10px] uppercase tracking-wide bg-surface border border-gray-200 text-gray-500 px-1.5 py-0.5 rounded">{f}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-center gap-3 shrink-0">
                <button onClick={() => openEdit(p)} className="text-gray-500 hover:text-pink" data-testid={`edit-product-mobile-${p.slug}`} aria-label="Edit"><Pencil className="w-5 h-5" /></button>
                <button onClick={() => del(p.id)} className="text-gray-500 hover:text-red-600" data-testid={`delete-product-mobile-${p.slug}`} aria-label="Delete"><Trash2 className="w-5 h-5" /></button>
              </div>
            </div>
          );
        })}
        {live.length === 0 && <p className="text-gray-400 text-sm">No products yet.</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-4 overflow-y-auto overscroll-contain">
          <div className="absolute inset-0 bg-black/50" onClick={() => setEditing(null)} />
          <div className="relative bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto overscroll-contain p-6 my-auto" style={{ WebkitOverflowScrolling: "touch" }} data-lenis-prevent data-testid="product-form">
            <div className="flex justify-between items-center mb-5">
              <h3 className="font-serif text-2xl">{editing.id ? "Edit" : "New"} Product</h3>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name"><input className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="product-name" /></Field>
              <Field label="Subtitle"><input className={inputCls} value={editing.subtitle} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} /></Field>
              <Field label="Category">
                <select className={inputCls} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Price (£)"><input type="number" step="0.01" className={inputCls} value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="product-price" /></Field>
              <Field label="Original Price (£, optional)"><input type="number" step="0.01" className={inputCls} value={editing.original_price} onChange={(e) => {
                const v = e.target.value;
                setEditing((prev) => ({
                  ...prev,
                  original_price: v,
                  on_sale: v !== "" && parseFloat(v) > parseFloat(prev.price || 0) ? true : prev.on_sale,
                }));
                const op = parseFloat(v); const pr = parseFloat(editing.price);
                setDiscInput(op > 0 && pr >= 0 && op >= pr ? String(Math.round((1 - pr / op) * 100)) : "");
              }} data-testid="product-original-price" /></Field>
              <Field label="Discount (% off original)">
                <div className="flex items-center gap-1">
                  <input
                    type="number" min="0" max="99" step="1"
                    className={inputCls}
                    disabled={!(parseFloat(editing.original_price) > 0)}
                    value={discInput}
                    onChange={(e) => {
                      const pct = e.target.value;
                      setDiscInput(pct);
                      const op = parseFloat(editing.original_price);
                      if (op > 0 && pct !== "") {
                        const newPrice = Math.round(op * (1 - Number(pct) / 100) * 100) / 100;
                        setEditing((prev) => ({ ...prev, price: newPrice, on_sale: Number(pct) > 0 }));
                      }
                    }}
                    data-testid="product-discount-percent"
                    placeholder={parseFloat(editing.original_price) > 0 ? "e.g. 25" : "Set original price first"}
                  />
                  <span className="text-xs text-gray-400">% off</span>
                </div>
              </Field>
              {!hasColours && !hasSizes && (
                <Field label="Stock"><input type="number" className={inputCls} value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} data-testid="product-stock" /></Field>
              )}
              {(hasColours || hasSizes) && (
                <Field label="Total Stock (auto)">
                  <input className={inputCls + " bg-surface"} disabled value={matrixTotal()} data-testid="product-total-stock" />
                </Field>
              )}
              <div className="sm:col-span-2"><Field label="Hover Media (image or video shown when hovering the card, optional)"><HoverMediaUpload image={editing.hover_image || ""} video={editing.hover_video || ""} onChange={({ image, video }) => setEditing({ ...editing, hover_image: image, hover_video: video })} /></Field></div>

              <div className="sm:col-span-2">
                <span className="text-xs uppercase tracking-wide text-gray-500">Colours</span>
                <p className="text-[11px] text-gray-400 mt-0.5">Select the colours this product comes in.</p>
                <div className="flex flex-wrap gap-2.5 mt-3" data-testid="admin-colour-picker">
                  {COLOUR_PALETTE.map((c) => {
                    const active = editing.colour_stock.some((x) => x.colour === c.name);
                    return (
                      <button key={c.name} type="button" title={c.name} onClick={() => toggleColour(c.name)}
                        data-testid={`admin-colour-${c.name}`}
                        className={`relative w-8 h-8 rounded-full transition-transform hover:scale-110 ${active ? "ring-2 ring-pink ring-offset-2" : "ring-1 ring-gray-200"} ${isLightColour(c.hex) ? "border border-gray-300" : ""}`}
                        style={{ backgroundColor: c.hex }}>
                        {active && <Check className={`w-4 h-4 absolute inset-0 m-auto ${isLightColour(c.hex) ? "text-charcoal" : "text-white"}`} />}
                      </button>
                    );
                  })}
                </div>
                {editing.colour_stock.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="admin-colour-stock-list">
                    {editing.colour_stock.map((c) => (
                      <span key={c.colour} className="inline-flex items-center gap-1.5 border border-gray-200 rounded-full pl-2 pr-1 py-1 text-xs">
                        <span className="w-3.5 h-3.5 rounded-full ring-1 ring-gray-200" style={{ backgroundColor: colourHex(c.colour) }} />
                        {c.colour}
                        <button type="button" onClick={() => toggleColour(c.colour)} className="text-gray-400 hover:text-pink"><X className="w-3.5 h-3.5" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {editing.colour_stock.length > 0 && (
                <div className="sm:col-span-2" data-testid="admin-colour-images">
                  <span className="text-xs uppercase tracking-wide text-gray-500">Product Images per Colour</span>
                  <p className="text-[11px] text-gray-400 mt-0.5">Attach photos for each colour. When a shopper picks a colour on the product page, the gallery switches to that colour's photos. Pick a cover below.</p>
                  <div className="mt-3 space-y-4">
                    {editing.colour_stock.map((c) => (
                      <div key={c.colour} className="border border-gray-200 rounded-md p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-4 h-4 rounded-full ring-1 ring-gray-200" style={{ backgroundColor: colourHex(c.colour) }} />
                          <span className="text-sm font-medium text-charcoal">{c.colour}</span>
                        </div>
                        <MultiImageUpload
                          value={colourImagesFor(c.colour)}
                          onChange={(v) => setColourImages(c.colour, v)}
                        />
                      </div>
                    ))}
                  </div>
                  {(() => {
                    const flat = [...new Set((editing.colour_images || []).flatMap((c) => c.images).filter(Boolean))];
                    if (flat.length === 0) return null;
                    const cover = (editing.image && flat.includes(editing.image)) ? editing.image : flat[0];
                    return (
                      <div className="mt-5" data-testid="admin-cover-picker">
                        <span className="text-xs uppercase tracking-wide text-gray-500">Cover Photo (shown on listing cards)</span>
                        <p className="text-[11px] text-gray-400 mt-0.5">Click a photo to make it the cover for this listing.</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {flat.map((img) => (
                            <button
                              type="button"
                              key={img}
                              onClick={() => setEditing({ ...editing, image: img })}
                              data-testid="admin-cover-option"
                              className={`relative w-16 h-20 rounded overflow-hidden border-2 transition-colors ${cover === img ? "border-pink" : "border-transparent hover:border-gray-300"}`}
                            >
                              <img src={img} alt="" className="w-full h-full object-cover bg-surface" />
                              {cover === img && (
                                <span className="absolute bottom-0 inset-x-0 bg-pink text-white text-[9px] text-center py-0.5">Cover</span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              <div className="sm:col-span-2">
                <span className="text-xs uppercase tracking-wide text-gray-500">Sizes</span>
                <p className="text-[11px] text-gray-400 mt-0.5">Pick standard letter sizes, or add custom labels like "Bust 36", "UK 12" or "Free Size".</p>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="admin-size-picker">
                  {SIZES.map((s) => {
                    const active = (editing.sizes || []).includes(s);
                    return (
                      <button key={s} type="button" onClick={() => toggleSize(s)} data-testid={`admin-size-${s}`}
                        className={`min-w-[48px] px-3 py-2 text-sm border rounded-sm transition-colors ${active ? "border-pink text-pink bg-pink/5" : "border-gray-200 text-gray-600 hover:border-charcoal"}`}>
                        {s}
                      </button>
                    );
                  })}
                </div>
                {(editing.sizes || []).filter((s) => !SIZES.includes(s)).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2.5" data-testid="admin-custom-sizes">
                    {(editing.sizes || []).filter((s) => !SIZES.includes(s)).map((s) => (
                      <span key={s} data-testid={`admin-custom-size-${s}`}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-pink text-pink bg-pink/5 rounded-sm">
                        {s}
                        <button type="button" onClick={() => removeSize(s)} aria-label={`Remove ${s}`} className="hover:text-pink-hover">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="mt-3 p-3 border border-gray-200 rounded-sm bg-gray-50/60" data-testid="admin-measurement-builder">
                  <span className="text-[11px] uppercase tracking-wide text-gray-500">Add by measurements (inches)</span>
                  <div className="flex flex-wrap items-end gap-2 mt-2">
                    {["bust", "waist", "hips"].map((k) => (
                      <label key={k} className="block">
                        <span className="text-[10px] uppercase tracking-wide text-gray-400 capitalize">{k}</span>
                        <input
                          type="number" min="0" step="0.5"
                          value={measure[k]}
                          onChange={(e) => setMeasure({ ...measure, [k]: e.target.value })}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addMeasurementSize(); } }}
                          placeholder='0"'
                          data-testid={`admin-measure-${k}`}
                          className="w-20 border border-gray-200 px-2 py-1.5 text-sm text-center focus:outline-none focus:border-pink block mt-0.5"
                        />
                      </label>
                    ))}
                    <button type="button" onClick={addMeasurementSize} data-testid="admin-add-measurement-size" className={btnDark}>
                      Add Size
                    </button>
                  </div>
                </div>
              </div>

              {(hasColours || hasSizes) && (
                <div className="sm:col-span-2">
                  <span className="text-xs uppercase tracking-wide text-gray-500">Stock Allocation</span>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    Set available stock for each {hasColours && hasSizes ? "colour & size" : hasColours ? "colour" : "size"} combination.
                  </p>
                  <div className="mt-3 overflow-x-auto" data-testid="admin-stock-matrix">
                    <table className="text-sm border-collapse">
                      {hasColours && hasSizes && (
                        <thead>
                          <tr>
                            <th className="p-1.5 text-left font-normal"></th>
                            {editing.sizes.map((s) => (
                              <th key={s} className="p-1.5 text-[11px] text-gray-500 font-medium w-16 text-center">{s}</th>
                            ))}
                          </tr>
                        </thead>
                      )}
                      <tbody>
                        {hasColours
                          ? editing.colour_stock.map((c) => (
                              <tr key={c.colour}>
                                <td className="p-1.5 pr-3 whitespace-nowrap">
                                  <span className="inline-flex items-center gap-2">
                                    <span className="w-4 h-4 rounded-full ring-1 ring-gray-200" style={{ backgroundColor: colourHex(c.colour) }} />
                                    <span className="text-xs">{c.colour}</span>
                                  </span>
                                </td>
                                {hasSizes
                                  ? editing.sizes.map((s) => (
                                      <td key={s} className="p-1">
                                        <input type="number" min="0" placeholder="0" value={getCell(c.colour, s)}
                                          onChange={(e) => setCell(c.colour, s, e.target.value)}
                                          data-testid={`admin-stock-${c.colour}-${s}`}
                                          className="w-14 border border-gray-200 px-2 py-1.5 text-sm text-center focus:outline-none focus:border-pink" />
                                      </td>
                                    ))
                                  : (
                                    <td className="p-1">
                                      <input type="number" min="0" placeholder="0" value={getCell(c.colour, null)}
                                        onChange={(e) => setCell(c.colour, null, e.target.value)}
                                        data-testid={`admin-stock-${c.colour}`}
                                        className="w-24 border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-pink" />
                                    </td>
                                  )}
                              </tr>
                            ))
                          : editing.sizes.map((s) => (
                              <tr key={s}>
                                <td className="p-1.5 pr-3 text-xs whitespace-nowrap">{s}</td>
                                <td className="p-1">
                                  <input type="number" min="0" placeholder="0" value={getCell(null, s)}
                                    onChange={(e) => setCell(null, s, e.target.value)}
                                    data-testid={`admin-stock-${s}`}
                                    className="w-24 border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-pink" />
                                </td>
                              </tr>
                            ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="sm:col-span-2 border border-gray-200 bg-gray-50/60 p-3" data-testid="admin-codes">
                <span className="text-xs uppercase tracking-wide text-gray-500 flex items-center gap-1.5"><Barcode className="w-3.5 h-3.5" /> SKUs &amp; barcodes</span>
                {(editing.variants || []).filter((v) => v.active).length ? (
                  <ul className="mt-2 space-y-1 text-xs">
                    {editing.variants.filter((v) => v.active).map((v) => (
                      <li key={v.id} className="flex flex-wrap gap-x-3 font-mono text-gray-600">
                        <span className="font-sans text-charcoal min-w-[90px]">{[v.colour, v.size].filter(Boolean).join(" · ") || "One size"}</span>
                        {v.barcode ? <><span>{v.sku}</span><span className="text-gray-400">{v.barcode}</span></>
                          : <span className="font-sans text-amber-700">No barcode yet</span>}
                      </li>
                    ))}
                  </ul>
                ) : null}
                <p className="text-[11px] text-gray-400 mt-2">Website products don't get codes here. In the inventory, open <b>Barcode stickers</b> to give each colour/size a new barcode, or assign it to an item you already have in the shop.</p>
              </div>

              <div className="sm:col-span-2"><Field label="Description"><textarea rows={3} className={inputCls} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></Field></div>
              <div className="sm:col-span-2">
                <span className="text-xs uppercase tracking-wide text-gray-500">Listing Badges</span>
                <p className="text-[11px] text-gray-400 mt-0.5">Highlight labels shown on the product card.</p>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="admin-badge-picker">
                  {PRODUCT_BADGE_OPTIONS.map((b) => {
                    const active = (editing.badges || []).includes(b.value);
                    return (
                      <button key={b.value} type="button" data-testid={`admin-badge-${b.value}`}
                        onClick={() => {
                          const cur = editing.badges || [];
                          setEditing({ ...editing, badges: active ? cur.filter((x) => x !== b.value) : [...cur, b.value] });
                        }}
                        className={`px-3 py-1.5 text-xs uppercase tracking-wide border rounded-full transition-colors ${active ? "border-pink text-pink bg-pink/5" : "border-gray-200 text-gray-600 hover:border-charcoal"}`}>
                        {b.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <Field label="Basket Count ('In X baskets', 0 = hidden)">
                <input type="number" min="0" className={inputCls} value={editing.basket_count}
                  onChange={(e) => setEditing({ ...editing, basket_count: e.target.value })}
                  data-testid="product-basket-count" />
              </Field>

              <div className="sm:col-span-2 border border-gray-200 rounded-lg p-4" data-testid="admin-alterable-section">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!editing.alterable}
                    onChange={(e) => setEditing({ ...editing, alterable: e.target.checked })}
                    data-testid="admin-alterable-toggle"
                    className="w-4 h-4 accent-pink"
                  />
                  <span className="text-sm font-medium text-charcoal">Alterable (Customise Fit)</span>
                </label>
                <p className="text-[11px] text-gray-400 mt-1">When enabled, customers can tailor Bust, Waist &amp; Hips. The allowance below is the maximum inches they can go up or down by on each measurement.</p>
                {editing.alterable && (
                  <div className="mt-3 flex flex-wrap items-end gap-5" data-testid="admin-alteration-allowance">
                    <div>
                      <span className="text-[11px] text-gray-500">Direction</span>
                      <select
                        value={editing.alteration_direction || "both"}
                        onChange={(e) => setEditing({ ...editing, alteration_direction: e.target.value })}
                        data-testid="admin-alter-direction"
                        className="block mt-0.5 border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:border-pink bg-white"
                      >
                        <option value="both">Larger or smaller (±)</option>
                        <option value="smaller">Smaller only (−)</option>
                        <option value="larger">Larger only (+)</option>
                      </select>
                    </div>
                    {(editing.alteration_direction || "both") !== "smaller" && (
                      <div>
                        <span className="text-[11px] text-gray-500">Can be made larger by</span>
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="text-xs text-gray-400">+</span>
                          <input
                            type="number" min="0" step="1"
                            value={editing.alteration_up ?? ""}
                            onChange={(e) => setEditing({ ...editing, alteration_up: e.target.value === "" ? "" : Number(e.target.value) })}
                            data-testid="admin-alter-up"
                            placeholder="e.g. 1"
                            className="w-20 border border-gray-200 px-2 py-1.5 text-sm text-center focus:outline-none focus:border-pink"
                          />
                          <span className="text-xs text-gray-400">inches</span>
                        </div>
                      </div>
                    )}
                    {(editing.alteration_direction || "both") !== "larger" && (
                      <div>
                        <span className="text-[11px] text-gray-500">Can be made smaller by</span>
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="text-xs text-gray-400">−</span>
                          <input
                            type="number" min="0" step="1"
                            value={editing.alteration_down ?? ""}
                            onChange={(e) => setEditing({ ...editing, alteration_down: e.target.value === "" ? "" : Number(e.target.value) })}
                            data-testid="admin-alter-down"
                            placeholder="e.g. 3"
                            className="w-20 border border-gray-200 px-2 py-1.5 text-sm text-center focus:outline-none focus:border-pink"
                          />
                          <span className="text-xs text-gray-400">inches</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="sm:col-span-2 flex flex-wrap gap-5 text-sm">
                {["featured"].map((f) => (
                  <label key={f} className="flex items-center gap-2">
                    <input type="checkbox" checked={!!editing[f]} onChange={(e) => setEditing({ ...editing, [f]: e.target.checked })} /> {f.replace("_", " ")}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-xs uppercase tracking-wide border border-gray-300">Cancel</button>
              <button onClick={save} className={btnPink} data-testid="product-save">Save Product</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Offers ----------------
const emptyOffer = { title: "", description: "", type: "percentage", value: 0, category: "", code: "", active: true, hidden: false };
function Offers({ api }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const load = useCallback(() => { api().get("/admin/offers").then(({ data }) => setItems(data)); }, [api]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const body = { ...form, value: parseFloat(form.value) || 0, category: form.category || null, code: form.code || null };
    delete body.id; delete body.created_at; delete body.last_sent_at; delete body.sent_count;
    try {
      if (form.id) await api().put(`/admin/offers/${form.id}`, body);
      else await api().post("/admin/offers", body);
      toast.success("Offer saved"); setForm(null); load();
    } catch (e) { toast.error("Save failed"); }
  };
  const del = async (id) => {
    try { await api().delete(`/admin/offers/${id}`); toast.success("Offer deleted"); setConfirmId(null); load(); }
    catch { toast.error("Delete failed"); }
  };
  const send = async (id) => {
    toast.message("Sending emails...");
    try {
      const { data } = await api().post(`/admin/offers/${id}/send`);
      if (data.configured === false || data.needs_domain_verification) toast.error(data.message, { duration: 9000 });
      else if (data.sent < (data.total || 0)) toast.warning(data.message, { duration: 9000 });
      else toast.success(data.message);
      load();
    } catch { toast.error("Send failed"); }
  };

  return (
    <div data-testid="admin-offers">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif text-3xl">Offers</h2>
        <button onClick={() => setForm({ ...emptyOffer })} className={btnPink + " flex items-center gap-2"} data-testid="admin-add-offer"><Plus className="w-4 h-4" /> New Offer</button>
      </div>
      <div className="space-y-3">
        {items.map((o) => (
          <div key={o.id} className="bg-white border border-gray-100 p-4 flex flex-wrap justify-between items-center gap-3">
            <div>
              <div className="font-medium">{o.title} <span className="text-xs uppercase text-gray-400">· {o.type}{o.value ? ` ${o.value}%` : ""}</span>
                {o.hidden && <span className="ml-2 text-[10px] uppercase tracking-wide bg-gray-200 text-gray-600 px-2 py-0.5 rounded" data-testid={`offer-hidden-badge-${o.id}`}>Hidden</span>}
                {o.active === false && <span className="ml-2 text-[10px] uppercase tracking-wide bg-gray-100 text-gray-400 px-2 py-0.5 rounded">Inactive</span>}
              </div>
              <div className="text-sm text-gray-500">{o.description}</div>
              <div className="text-xs text-gray-400 mt-1">Sent {o.sent_count || 0} time(s){o.code ? ` · code ${o.code}` : ""}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => send(o.id)} className={btnDark + " flex items-center gap-1"} data-testid={`send-offer-${o.id}`}><Send className="w-3.5 h-3.5" /> Send</button>
              <button onClick={() => setForm({ ...o, category: o.category || "", code: o.code || "" })} className="px-3 py-2 border border-gray-300 text-xs"><Pencil className="w-3.5 h-3.5" /></button>
              {confirmId === o.id ? (
                <>
                  <button onClick={() => del(o.id)} data-testid={`offer-delete-confirm-${o.id}`} className="px-3 py-2 text-xs bg-red-600 text-white hover:bg-red-700 transition-colors">Confirm</button>
                  <button onClick={() => setConfirmId(null)} className="px-3 py-2 text-xs border border-gray-300 hover:bg-gray-50 transition-colors">Cancel</button>
                </>
              ) : (
                <button onClick={() => setConfirmId(o.id)} data-testid={`offer-delete-${o.id}`} className="px-3 py-2 border border-gray-300 text-xs hover:text-red-600 hover:border-red-300 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-gray-400">No offers yet.</p>}
      </div>

      {form && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setForm(null)} />
          <div className="relative bg-white w-full max-w-lg p-6" data-testid="offer-form">
            <div className="flex justify-between items-center mb-5"><h3 className="font-serif text-2xl">{form.id ? "Edit" : "New"} Offer</h3><button onClick={() => setForm(null)}><X className="w-5 h-5" /></button></div>
            <div className="space-y-4">
              <Field label="Title"><input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="offer-title" /></Field>
              <Field label="Description"><textarea rows={2} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Type">
                  <select className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                    <option value="percentage">Percentage off</option>
                    <option value="sitewide">Sitewide sale</option>
                    <option value="category">Category</option>
                    <option value="announcement">Announcement</option>
                  </select>
                </Field>
                <Field label="Value (%)"><input type="number" className={inputCls} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></Field>
                <Field label="Category (optional)">
                  <select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    <option value="">-</option>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Code (optional)"><input className={inputCls} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Field>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
              <label className="flex items-center gap-2 text-sm" data-testid="offer-hidden-toggle"><input type="checkbox" checked={!!form.hidden} onChange={(e) => setForm({ ...form, hidden: e.target.checked })} /> Hidden (not shown on the Offers page)</label>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setForm(null)} className="px-4 py-2 text-xs uppercase border border-gray-300">Cancel</button>
              <button onClick={save} className={btnPink} data-testid="offer-save">Save Offer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Discount codes ----------------
const emptyCode = { code: "", type: "percentage", value: 0, description: "", active: true, usage_limit: "" };
function Discounts({ api }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const load = useCallback(() => { api().get("/admin/discount-codes").then(({ data }) => setItems(data)); }, [api]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const body = { ...form, value: parseFloat(form.value) || 0, usage_limit: form.usage_limit === "" ? null : parseInt(form.usage_limit) };
    delete body.id; delete body.used_count; delete body.created_at; delete body.kind; delete body.offer_id;
    try {
      if (form.id) await api().put(`/admin/discount-codes/${form.id}`, body);
      else await api().post("/admin/discount-codes", body);
      toast.success("Code saved"); setForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => {
    try { await api().delete(`/admin/discount-codes/${id}`); toast.success("Code deleted"); setConfirmId(null); load(); }
    catch { toast.error("Delete failed"); }
  };
  const toggleActive = async (c) => {
    try { await api().patch(`/admin/discount-codes/${c.id}/active`, { active: !c.active }); toast.success(c.active ? "Code deactivated" : "Code activated"); load(); }
    catch { toast.error("Update failed"); }
  };

  const kindBadge = (c) => {
    if (c.kind === "welcome") return <span className="ml-2 text-[10px] uppercase tracking-wide bg-metallic-gold text-charcoal px-2 py-0.5 rounded-full font-semibold" data-testid={`code-welcome-badge-${c.id}`}>Welcome Popup</span>;
    if (c.kind === "offer") return <span className="ml-2 text-[10px] uppercase tracking-wide bg-pink/10 text-pink px-2 py-0.5 rounded-full" data-testid={`code-offer-badge-${c.id}`}>From Offer</span>;
    if (c.kind === "reward") return <span className="ml-2 text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Reward</span>;
    return null;
  };

  const order = { welcome: 0, offer: 1, reward: 2 };
  const sorted = [...items].sort((a, b) => (order[a.kind] ?? 3) - (order[b.kind] ?? 3));

  return (
    <div data-testid="admin-discounts">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif text-3xl">Discount Codes</h2>
        <button onClick={() => setForm({ ...emptyCode })} className={btnPink + " flex items-center gap-2"} data-testid="admin-add-code"><Plus className="w-4 h-4" /> New Code</button>
      </div>
      <div className="bg-white border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-surface text-left text-xs uppercase tracking-wide text-gray-500"><th className="px-4 py-3">Code</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Value</th><th className="px-4 py-3">Used</th><th className="px-4 py-3">Active</th><th className="px-4 py-3"></th></tr></thead>
          <tbody>
            {sorted.map((c) => (
              <tr key={c.id} className={`border-t border-gray-100 ${c.kind === "welcome" ? "bg-amber-50/40" : ""}`} data-testid={`code-row-${c.code}`}>
                <td className="px-4 py-3 font-medium tracking-wide">{c.code}{kindBadge(c)}</td>
                <td className="px-4 py-3">{c.type}</td>
                <td className="px-4 py-3">{c.type === "percentage" ? `${c.value}%` : formatGBP(c.value)}</td>
                <td className="px-4 py-3">{c.used_count || 0}{c.usage_limit ? `/${c.usage_limit}` : ""}</td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleActive(c)} data-testid={`code-active-toggle-${c.code}`}
                    className={`text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full transition-colors ${c.active ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-gray-200 text-gray-500 hover:bg-gray-300"}`}>
                    {c.active ? "Active" : "Inactive"}
                  </button>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {confirmId === c.id ? (
                    <span className="flex items-center justify-end gap-2">
                      <button onClick={() => del(c.id)} data-testid={`code-delete-confirm-${c.code}`} className="text-xs bg-red-600 text-white px-2.5 py-1.5 hover:bg-red-700 transition-colors">Confirm</button>
                      <button onClick={() => setConfirmId(null)} className="text-xs border border-gray-300 px-2.5 py-1.5 hover:bg-gray-50 transition-colors">Cancel</button>
                    </span>
                  ) : (
                    <>
                      <button onClick={() => setForm({ ...c, usage_limit: c.usage_limit ?? "" })} data-testid={`code-edit-${c.code}`} className="text-gray-500 hover:text-pink mr-3"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => setConfirmId(c.id)} data-testid={`code-delete-${c.code}`} className="text-gray-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setForm(null)} />
          <div className="relative bg-white w-full max-w-md p-6" data-testid="code-form">
            <div className="flex justify-between items-center mb-5"><h3 className="font-serif text-2xl">{form.id ? "Edit" : "New"} Code</h3><button onClick={() => setForm(null)}><X className="w-5 h-5" /></button></div>
            <div className="space-y-4">
              <Field label="Code"><input className={inputCls + " uppercase"} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="code-code" /></Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Type"><select className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}><option value="percentage">Percentage</option><option value="fixed">Fixed £</option></select></Field>
                <Field label="Value"><input type="number" className={inputCls} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="code-value" /></Field>
              </div>
              <Field label="Description"><input className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
              <Field label="Usage limit (optional)"><input type="number" className={inputCls} value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} /></Field>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setForm(null)} className="px-4 py-2 text-xs uppercase border border-gray-300">Cancel</button>
              <button onClick={save} className={btnPink} data-testid="code-save">Save Code</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Customers ----------------
function Customers({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const deleteCustomer = async (id) => {
    try {
      await api().delete(`/admin/customers/${id}`);
      toast.success("Customer account deleted");
      setConfirmId(null);
      setItems((prev) => prev.filter((c) => c.id !== id));
    } catch {
      toast.error("Failed to delete customer");
    }
  };

  useEffect(() => {
    api().get("/admin/customers")
      .then(({ data }) => setItems(data))
      .catch(() => toast.error("Failed to load customers"))
      .finally(() => setLoading(false));
  }, [api]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({});
    try {
      const { data } = await api().get(`/admin/customers/${id}`);
      setDetail(data);
    } catch {
      toast.error("Failed to load customer details");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const filtered = items.filter((c) =>
    !query || c.name?.toLowerCase().includes(query.toLowerCase()) || c.email?.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div data-testid="admin-customers">
      <h2 className="font-serif text-3xl mb-2">Customers <span className="text-gray-400 text-lg">({items.length})</span></h2>
      <div className="flex items-start gap-2 text-xs text-gray-500 bg-surface border border-gray-200 rounded-md px-4 py-3 mb-5">
        <ShieldCheck className="w-4 h-4 text-metallic-gold shrink-0 mt-0.5" />
        <p>For security, passwords are stored <strong>encrypted (hashed)</strong> and can never be viewed — not even by admins. Customers reset their own password via the "forgot password" email if needed.</p>
      </div>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search by name or email..."
        data-testid="customer-search"
        className="w-full sm:w-80 border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-pink mb-5"
      />
      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3 w-28">Sign-in</th>
                <th className="px-4 py-3 text-right w-24">Orders</th>
                <th className="px-4 py-3 text-right w-28">Spent</th>
                <th className="px-4 py-3 text-right w-28">Rewards</th>
                <th className="px-4 py-3 w-32">Joined</th>
                <th className="px-4 py-3 text-right w-36">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.email}`} className="hover:bg-surface/50">
                  <td className="px-4 py-3 font-medium text-charcoal whitespace-nowrap">{c.name || "—"}</td>
                  <td className="px-4 py-3 text-gray-600">{c.email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full ${c.auth_provider === "google" ? "bg-blue-50 text-blue-600" : "bg-pink/10 text-pink"}`}>
                      {c.auth_provider === "google" ? "Google" : "Email"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{c.orders}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatGBP(c.spent)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-metallic-gold" data-testid={`customer-rewards-${c.email}`}>{formatGBP(c.rewards_balance || 0)}</td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{c.created_at ? new Date(c.created_at).toLocaleDateString("en-GB") : "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {confirmId === c.id ? (
                      <span className="flex items-center justify-end gap-2">
                        <button onClick={() => deleteCustomer(c.id)} data-testid={`customer-delete-confirm-${c.email}`}
                          className="text-xs bg-red-600 text-white px-2.5 py-1.5 hover:bg-red-700 transition-colors">Confirm</button>
                        <button onClick={() => setConfirmId(null)}
                          className="text-xs border border-gray-300 px-2.5 py-1.5 hover:bg-gray-50 transition-colors">Cancel</button>
                      </span>
                    ) : (
                      <span className="flex items-center justify-end gap-2">
                        <button onClick={() => openDetail(c.id)} data-testid={`customer-view-${c.email}`}
                          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wide border border-charcoal px-3 py-1.5 hover:bg-pink hover:border-pink hover:text-white transition-colors">
                          View
                        </button>
                        <button onClick={() => setConfirmId(c.id)} data-testid={`customer-delete-${c.email}`}
                          title="Delete account" className="text-gray-400 hover:text-red-600 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td className="px-4 py-6 text-gray-400" colSpan={8}>No customers found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="customer-detail-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDetail(null)} />
          <div className="relative bg-white w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-lg shadow-2xl" data-lenis-prevent>
            <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-serif text-2xl">Customer Details</h3>
              <button onClick={() => setDetail(null)} data-testid="customer-detail-close" className="text-gray-400 hover:text-pink"><X className="w-5 h-5" /></button>
            </div>
            {detailLoading || !detail.id ? (
              <div className="p-10 flex items-center gap-2 text-gray-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
            ) : (
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><p className="text-xs uppercase tracking-wide text-gray-400">Name</p><p className="text-charcoal font-medium">{detail.name || "—"}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-gray-400">Email</p><p className="text-charcoal">{detail.email}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-gray-400">Sign-in Method</p><p className="text-charcoal capitalize">{detail.auth_provider === "google" ? "Google" : "Email & Password"}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-gray-400">Email Verified</p><p className="text-charcoal">{detail.verified ? "Yes" : "No"}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-gray-400">Joined</p><p className="text-charcoal">{detail.created_at ? new Date(detail.created_at).toLocaleString("en-GB") : "—"}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-gray-400">Wishlist Items</p><p className="text-charcoal">{detail.wishlist_count}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-gray-400">Rewards Balance</p><p className="text-metallic-gold font-semibold" data-testid="customer-detail-rewards">{formatGBP(detail.rewards_balance || 0)}</p></div>
                </div>

                <div className="flex items-start gap-2 text-xs text-gray-500 bg-surface border border-gray-200 rounded-md px-4 py-3">
                  <ShieldCheck className="w-4 h-4 text-metallic-gold shrink-0 mt-0.5" />
                  <p><strong>Password:</strong> encrypted and not viewable by anyone (industry-standard security). Ask the customer to use "Forgot password" to reset it.</p>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Saved Addresses ({detail.addresses.length})</p>
                  {detail.addresses.length === 0 ? (
                    <p className="text-sm text-gray-400">No saved addresses.</p>
                  ) : (
                    <div className="space-y-2">
                      {detail.addresses.map((a, i) => (
                        <div key={i} className="border border-gray-200 rounded-md p-3 text-sm text-gray-600" data-testid={`customer-address-${i}`}>
                          {a.name && <p className="text-charcoal font-medium">{a.name}</p>}
                          <p>{[a.line1, a.line2, a.city, a.postcode, a.country].filter(Boolean).join(", ")}</p>
                          {a.phone && <p className="text-gray-400">{a.phone}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Order History ({detail.orders.length})</p>
                  {detail.orders.length === 0 ? (
                    <p className="text-sm text-gray-400">No orders yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {detail.orders.map((o) => (
                        <div key={o.order_number || o.id} className="border border-gray-200 rounded-md p-3 flex items-center justify-between text-sm" data-testid={`customer-order-${o.order_number || o.id}`}>
                          <div>
                            <p className="text-charcoal font-medium">#{o.order_number || o.id}</p>
                            <p className="text-xs text-gray-400">{o.created_at ? new Date(o.created_at).toLocaleDateString("en-GB") : ""} · {(o.items || []).length} item(s) · {o.status || "—"}</p>
                          </div>
                          <span className="font-medium">{formatGBP(o.total)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}



// ---------------- Subscribers ----------------
function Subscribers({ api }) {
  const [items, setItems] = useState([]);
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState(null);
  const load = useCallback(() => {
    api().get("/admin/subscribers").then(({ data }) => setItems(data)).catch(() => toast.error("Failed to load subscribers"));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const value = email.trim();
    if (!value) return;
    setAdding(true);
    try {
      await api().post("/admin/subscribers", { email: value, source: "admin" });
      toast.success("Subscriber added");
      setEmail(""); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add subscriber");
    } finally { setAdding(false); }
  };

  const remove = async (subEmail) => {
    try {
      await api().delete(`/admin/subscribers/${encodeURIComponent(subEmail)}`);
      toast.success("Subscriber removed");
      setConfirmEmail(null);
      load();
    } catch { toast.error("Could not remove subscriber"); }
  };

  return (
    <div data-testid="admin-subscribers">
      <h2 className="font-serif text-3xl mb-6">Subscribers <span className="text-gray-400 text-lg">({items.length})</span></h2>

      <div className="bg-white border border-gray-100 p-4 mb-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <span className="text-xs uppercase tracking-wide text-gray-500">Add subscriber email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            data-testid="subscriber-email-input"
            placeholder="name@example.com"
            className={inputCls + " mt-1"}
          />
        </div>
        <button onClick={add} disabled={adding} className={btnPink + " flex items-center gap-2"} data-testid="subscriber-add-btn">
          <Plus className="w-4 h-4" /> {adding ? "Adding..." : "Add"}
        </button>
      </div>

      <div className="bg-white border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-surface text-left text-xs uppercase tracking-wide text-gray-500"><th className="px-4 py-3">Email</th><th className="px-4 py-3">Source</th><th className="px-4 py-3">Date</th><th className="px-4 py-3"></th></tr></thead>
          <tbody>
            {items.map((s, i) => (
              <tr key={i} className="border-t border-gray-100" data-testid={`subscriber-row-${i}`}>
                <td className="px-4 py-3">{s.email}</td>
                <td className="px-4 py-3 capitalize">{s.source || "-"}</td>
                <td className="px-4 py-3 text-gray-500">{(s.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {confirmEmail === s.email ? (
                    <span className="flex items-center justify-end gap-2">
                      <button onClick={() => remove(s.email)} data-testid={`subscriber-delete-confirm-${i}`}
                        className="text-xs bg-red-600 text-white px-2.5 py-1.5 hover:bg-red-700 transition-colors">Confirm</button>
                      <button onClick={() => setConfirmEmail(null)} data-testid={`subscriber-delete-cancel-${i}`}
                        className="text-xs border border-gray-300 px-2.5 py-1.5 hover:bg-gray-50 transition-colors">Cancel</button>
                    </span>
                  ) : (
                    <button onClick={() => setConfirmEmail(s.email)} className="text-gray-400 hover:text-red-600 transition-colors" aria-label="Remove subscriber" data-testid={`subscriber-delete-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td className="px-4 py-6 text-gray-400" colSpan={4}>No subscribers yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- Orders ----------------
const STATUS_META = {
  new: { label: "New", icon: Clock, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  dispatched: { label: "Dispatched", icon: Truck, cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  delivered: { label: "Delivered", icon: PackageCheck, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  ready_to_collect: { label: "Ready to Collect", icon: Store, cls: "bg-sky-50 text-sky-700 border-sky-200" },
  collected: { label: "Collected", icon: PackageCheck, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelled", icon: XCircle, cls: "bg-red-50 text-red-700 border-red-200" },
};
// Status options depend on how the order is being fulfilled.
const STATUSES_FOR = (order) => order?.fulfillment === "collect"
  ? ["new", "ready_to_collect", "collected", "cancelled"]
  : ["new", "dispatched", "delivered", "cancelled"];
// Statuses that trigger a customer notification email by default.
const EMAILABLE = ["dispatched", "delivered", "cancelled", "ready_to_collect"];

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, icon: Clock, cls: "bg-gray-50 text-gray-600 border-gray-200" };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border rounded-full ${meta.cls}`} data-testid={`status-badge-${status}`}>
      <Icon className="w-3.5 h-3.5" /> {meta.label}
    </span>
  );
};

function Orders({ api }) {
  const [items, setItems] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [modal, setModal] = useState(null); // { order, status, tracking, sendEmail }
  const [detail, setDetail] = useState(null); // full order being viewed
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const load = useCallback(() => { api().get("/admin/orders").then(({ data }) => setItems(data)); }, [api]);
  useEffect(() => { load(); }, [load]);

  const getInvoice = async (order) => {
    setInvoiceBusy(true);
    try {
      const { data } = await api().get(`/admin/orders/${order.order_number}/invoice`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${order.order_number}-invoice.pdf`; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not load invoice");
    } finally { setInvoiceBusy(false); }
  };
  const viewInvoice = async (order) => {
    setInvoiceBusy(true);
    try {
      const { data } = await api().get(`/admin/orders/${order.order_number}/invoice`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      toast.error("Could not load invoice");
    } finally { setInvoiceBusy(false); }
  };

  const filtered = items.filter((o) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (o.order_number || "").toLowerCase().includes(q)
      || (o.customer_email || "").toLowerCase().includes(q)
      || (o.shipping?.email || "").toLowerCase().includes(q)
      || (o.shipping?.full_name || "").toLowerCase().includes(q);
  });

  const openStatus = (order) => setModal({
    order,
    status: order.status,
    tracking: order.tracking_number || "",
    sendEmail: EMAILABLE.includes(order.status),
  });

  const applyStatus = async () => {
    setSaving(true);
    try {
      const { data } = await api().put(`/admin/orders/${modal.order.order_number}/status`, {
        status: modal.status,
        tracking_number: modal.tracking.trim() || null,
        send_email: modal.sendEmail,
      });
      toast.success(`Order ${modal.order.order_number} → ${STATUS_META[modal.status]?.label || modal.status}${data.email_sent ? ". Email sent." : "."}`);
      if (data.refunded > 0) toast.success(`${formatGBP(data.refunded)} refunded to the customer's card via Stripe.`);
      setModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status");
    } finally { setSaving(false); }
  };

  const deleteOrder = async (order) => {
    try {
      await api().delete(`/admin/orders/${order.order_number}`);
      toast.success(`Order ${order.order_number} deleted`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      setConfirmDelete(null);
      toast.error(e?.response?.data?.detail || "Could not delete order");
    }
  };

  const canEmail = modal && EMAILABLE.includes(modal.status);

  return (
    <div data-testid="admin-orders">
      <h2 className="font-serif text-3xl mb-4">Orders <span className="text-gray-400 text-lg">({items.length})</span></h2>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search by order number, name or email..."
        data-testid="order-search"
        className="w-full sm:w-96 border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-pink mb-5"
      />
      <div className="bg-white border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-surface text-left text-xs uppercase tracking-wide text-gray-500"><th className="px-4 py-3">Order</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Actions</th></tr></thead>
          <tbody>
            {filtered.map((o, i) => (
              <tr key={i} className="border-t border-gray-100">
                <td className="px-4 py-3 font-medium">
                  <button onClick={() => setDetail(o)} className="text-pink hover:underline" data-testid={`order-number-${o.order_number}`}>{o.order_number}</button>
                  {(o.has_custom_items || (o.items || []).some((it) => it.customization?.measurements?.length)) && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[9px] uppercase tracking-wide font-bold bg-amber-50 text-amber-700 border border-amber-300 px-1.5 py-0.5 rounded" data-testid={`order-custom-tag-${o.order_number}`}>
                      <Scissors className="w-2.5 h-2.5" /> Custom Alteration
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">{o.shipping?.full_name}<div className="text-xs text-gray-400">{o.customer_email || o.shipping?.email}</div></td>
                <td className="px-4 py-3">{formatGBP(o.total)}</td>
                <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                <td className="px-4 py-3 text-gray-500">{(o.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {confirmDelete === o.order_number ? (
                    <span className="flex items-center gap-2">
                      <button onClick={() => deleteOrder(o)} data-testid={`order-delete-confirm-${o.order_number}`}
                        className="text-xs bg-red-600 text-white px-2.5 py-1.5 hover:bg-red-700 transition-colors">Confirm</button>
                      <button onClick={() => setConfirmDelete(null)} data-testid={`order-delete-cancel-${o.order_number}`}
                        className="text-xs border border-gray-300 px-2.5 py-1.5 hover:bg-gray-50 transition-colors">Cancel</button>
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <button onClick={() => setDetail(o)} data-testid={`order-details-btn-${o.order_number}`}
                        className="text-xs border border-gray-300 px-3 py-1.5 hover:border-pink hover:text-pink transition-colors flex items-center gap-1.5">
                        <Eye className="w-3.5 h-3.5" /> Details
                      </button>
                      <button onClick={() => openStatus(o)} data-testid={`order-status-btn-${o.order_number}`}
                        className="text-xs border border-gray-300 px-3 py-1.5 hover:border-pink hover:text-pink transition-colors flex items-center gap-1.5">
                        <Pencil className="w-3.5 h-3.5" /> Update
                      </button>
                      <button onClick={() => setConfirmDelete(o.order_number)} data-testid={`order-delete-${o.order_number}`}
                        aria-label="Delete order" className="text-gray-400 hover:text-red-600 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td className="px-4 py-6 text-gray-400" colSpan={6}>{items.length === 0 ? "No orders yet." : "No orders match your search."}</td></tr>}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModal(null)} />
          <div className="relative bg-white w-full max-w-md p-6" data-testid="order-status-modal">
            <div className="flex justify-between items-center mb-1">
              <h3 className="font-serif text-2xl">Update Order Status</h3>
              <button onClick={() => setModal(null)}><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-gray-400 mb-5">{modal.order.order_number} · {modal.order.shipping?.full_name}</p>

            <span className="text-xs uppercase tracking-wide text-gray-500">Status</span>
            <div className="grid grid-cols-2 gap-2 mt-2 mb-5">
              {STATUSES_FOR(modal.order).map((s) => {
                const meta = STATUS_META[s];
                const Icon = meta.icon;
                const active = modal.status === s;
                const locked = modal.order.status === "cancelled" && s !== "cancelled";
                return (
                  <button key={s} type="button" disabled={locked}
                    onClick={() => setModal((m) => ({ ...m, status: s, sendEmail: EMAILABLE.includes(s) }))}
                    data-testid={`status-option-${s}`}
                    className={`flex items-center gap-2 px-3 py-2.5 text-xs border rounded-md transition-colors text-left disabled:opacity-40 disabled:cursor-not-allowed ${active ? "border-pink bg-pink/5 text-pink" : "border-gray-200 text-gray-600 hover:border-charcoal"}`}>
                    <Icon className="w-4 h-4 shrink-0" /> {meta.label}
                  </button>
                );
              })}
            </div>

            {modal.order.status === "cancelled" && (
              <p className="mb-4 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md p-3" data-testid="order-closed-note">
                This order was cancelled and refunded, so it can't be reopened.
              </p>
            )}
            {modal.status === "cancelled" && modal.order.status !== "cancelled" && (
              <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800 leading-relaxed" data-testid="order-cancel-warning">
                <p className="font-semibold text-sm mb-1">Cancel this order?</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>{modal.order.paid && modal.order.total > 0 ? `${formatGBP(modal.order.total)} is refunded to the customer's card through Stripe.` : "No payment was taken, so nothing is refunded."}</li>
                  <li>The items go back into stock (website and shop).</li>
                  <li>The customer gets a cancellation email if the box below is ticked.</li>
                  <li>This can't be undone.</li>
                </ul>
              </div>
            )}
            {modal.status === "dispatched" && (
              <div className="mb-4">
                <Field label="Royal Mail tracking number">
                  <input className={inputCls} value={modal.tracking}
                    onChange={(e) => setModal((m) => ({ ...m, tracking: e.target.value }))}
                    data-testid="order-tracking-input" placeholder="e.g. AB123456789GB" />
                </Field>
                <p className="text-[11px] text-gray-400 mt-1.5">Included in the dispatch email so the customer can track their parcel.</p>
              </div>
            )}

            <label className={`flex items-center gap-2.5 text-sm p-3 border rounded-md ${canEmail ? "border-gray-200" : "border-gray-100 opacity-50"}`}>
              <input type="checkbox" checked={canEmail && modal.sendEmail} disabled={!canEmail}
                onChange={(e) => setModal((m) => ({ ...m, sendEmail: e.target.checked }))}
                data-testid="order-send-email-toggle" />
              <span className="flex items-center gap-1.5"><Mail className="w-4 h-4 text-gray-500" /> {modal.status === "cancelled" ? "Send cancellation & refund email" : "Notify customer by email"}</span>
            </label>
            {!canEmail && <p className="text-[11px] text-gray-400 mt-1.5">No customer email is sent for this status.</p>}

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setModal(null)} className="px-4 py-2 text-xs uppercase border border-gray-300">Cancel</button>
              <button onClick={applyStatus} disabled={saving || modal.order.status === "cancelled"} data-testid="order-status-apply"
                className={modal.status === "cancelled" && modal.order.status !== "cancelled" ? "bg-red-600 text-white px-4 py-2 text-xs uppercase tracking-[0.12em] hover:bg-red-700 transition-colors disabled:opacity-50" : `${btnPink} disabled:opacity-50`}>
                {saving ? "Saving..." : modal.status === "cancelled" && modal.order.status !== "cancelled"
                  ? (modal.order.paid && modal.order.total > 0 ? `Cancel & refund ${formatGBP(modal.order.total)}` : "Cancel order")
                  : "Apply"}
              </button>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDetail(null)} />
          <div className="relative bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" data-lenis-prevent data-testid="order-detail-modal">
            <div className="flex justify-between items-start mb-1">
              <div>
                <h3 className="font-serif text-2xl">Order {detail.order_number}</h3>
                <p className="text-xs text-gray-400 mt-1">{(detail.created_at || "").slice(0, 16).replace("T", " ")}</p>
              </div>
              <button onClick={() => setDetail(null)}><X className="w-5 h-5" /></button>
            </div>

            <div className="mb-5 mt-2 flex flex-wrap items-center gap-2">
              <StatusBadge status={detail.status} />
              {(detail.has_custom_items || (detail.items || []).some((it) => it.customization?.measurements?.length)) && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold bg-amber-50 text-amber-700 border border-amber-300 px-2 py-1 rounded" data-testid="order-detail-custom-tag">
                  <Scissors className="w-3 h-3" /> Custom Alteration
                </span>
              )}
            </div>

            {/* Items */}
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Items ({(detail.items || []).length})</p>
            <div className="space-y-3 mb-6">
              {(detail.items || []).map((it, idx) => (
                <div key={idx} className="flex gap-3 border border-gray-100 p-3" data-testid={`order-detail-item-${idx}`}>
                  {it.image ? (
                    <img src={it.image} alt={it.name} className="w-16 h-20 object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-16 h-20 bg-surface flex items-center justify-center flex-shrink-0"><Package className="w-5 h-5 text-gray-300" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm leading-tight">{it.name}</p>
                    {it.subtitle && <p className="text-xs text-gray-400">{it.subtitle}</p>}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-600">
                      {it.colour && <span><span className="text-gray-400">Colour:</span> {it.colour}</span>}
                      {it.size && <span><span className="text-gray-400">Size:</span> {it.size}</span>}
                      <span><span className="text-gray-400">Qty:</span> {it.quantity}</span>
                    </div>
                    {it.customization?.measurements?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-100" data-testid={`order-detail-custom-${idx}`}>
                        <p className="text-[11px] uppercase tracking-wide text-pink font-semibold flex items-center gap-1"><Scissors className="w-3 h-3" /> Custom Fit</p>
                        <ul className="text-xs text-gray-600 mt-1 space-y-0.5">
                          {it.customization.measurements.map((m) => (
                            <li key={m.key}>
                              {m.label}: <span className="text-gray-400">{m.standard}"</span> → <span className="text-charcoal">{m.adjustment > 0 ? `+${m.adjustment}` : m.adjustment}"</span> → <span className="text-charcoal font-medium">{m.final}"</span>
                            </li>
                          ))}
                        </ul>
                        {it.custom_confirmed && (
                          <p className="text-[11px] text-emerald-600 mt-1 flex items-center gap-1"><Check className="w-3 h-3" /> Customer confirmed measurements</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-right text-sm whitespace-nowrap">
                    <div className="font-medium">{formatGBP(it.line_total ?? (it.price * it.quantity))}</div>
                    <div className="text-xs text-gray-400">{formatGBP(it.price)} each</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Shipping */}
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Shipping To</p>
            <div className="text-sm text-gray-700 border border-gray-100 p-3 mb-6 space-y-0.5" data-testid="order-detail-shipping">
              <p className="font-medium">{detail.shipping?.full_name}</p>
              {detail.shipping?.address_line1 && <p>{detail.shipping.address_line1}</p>}
              {detail.shipping?.address_line2 && <p>{detail.shipping.address_line2}</p>}
              <p>{[detail.shipping?.city, detail.shipping?.postcode].filter(Boolean).join(", ")}</p>
              {detail.shipping?.country && <p>{detail.shipping.country}</p>}
              <p className="pt-1 text-gray-500">{detail.customer_email || detail.shipping?.email}</p>
              {detail.shipping?.phone && <p className="text-gray-500">{detail.shipping.phone}</p>}
            </div>

            {/* Billing (only when different from shipping) */}
            {detail.billing && detail.billing_same_as_shipping === false && (
              <>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Billing Address</p>
                <div className="text-sm text-gray-700 border border-gray-100 p-3 mb-6 space-y-0.5" data-testid="order-detail-billing">
                  <p className="font-medium">{detail.billing.full_name}</p>
                  {detail.billing.address && <p>{detail.billing.address}</p>}
                  <p>{[detail.billing.city, detail.billing.postcode].filter(Boolean).join(", ")}</p>
                  {detail.billing.country && <p>{detail.billing.country}</p>}
                </div>
              </>
            )}

            {/* Tracking */}
            {detail.tracking_number && (
              <div className="mb-6">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Royal Mail Tracking</p>
                <p className="text-sm font-mono border border-dashed border-metallic-gold text-charcoal px-3 py-2 inline-block" data-testid="order-detail-tracking">{detail.tracking_number}</p>
              </div>
            )}

            {/* Totals */}
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Payment Summary</p>
            <div className="text-sm space-y-1.5 border border-gray-100 p-3" data-testid="order-detail-summary">
              <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{formatGBP(detail.subtotal)}</span></div>
              {detail.discount_amount > 0 && (
                <div className="flex justify-between text-emerald-600"><span>Discount{detail.discount_code ? ` (${detail.discount_code})` : ""}</span><span>-{formatGBP(detail.discount_amount)}</span></div>
              )}
              {detail.rewards_applied > 0 && (
                <div className="flex justify-between text-metallic-gold"><span>Rewards applied</span><span>-{formatGBP(detail.rewards_applied)}</span></div>
              )}
              <div className="flex justify-between"><span className="text-gray-500">Shipping</span><span>{detail.shipping_cost > 0 ? formatGBP(detail.shipping_cost) : "Free"}</span></div>
              <div className="flex justify-between font-semibold text-base pt-1.5 border-t border-gray-100"><span>Total</span><span>{formatGBP(detail.total)}</span></div>
            </div>

            {/* Invoice */}
            <div className="mt-6" data-testid="order-detail-invoice">
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                Invoice{detail.invoice_number ? ` · ${detail.invoice_number}` : ""}
              </p>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => viewInvoice(detail)} disabled={invoiceBusy} data-testid="order-invoice-view"
                  className="inline-flex items-center gap-1.5 text-sm border border-gray-200 rounded px-3 py-1.5 hover:border-charcoal transition-colors disabled:opacity-50">
                  <Eye className="w-3.5 h-3.5" /> View
                </button>
                <button onClick={() => getInvoice(detail)} disabled={invoiceBusy} data-testid="order-invoice-download"
                  className="inline-flex items-center gap-1.5 text-sm border border-gray-200 rounded px-3 py-1.5 hover:border-charcoal transition-colors disabled:opacity-50">
                  <Download className="w-3.5 h-3.5" /> Download
                </button>
                <button onClick={() => getInvoice(detail)} disabled={invoiceBusy} data-testid="order-invoice-regenerate"
                  className="inline-flex items-center gap-1.5 text-sm border border-gray-200 rounded px-3 py-1.5 hover:border-charcoal transition-colors disabled:opacity-50">
                  <Loader2 className={`w-3.5 h-3.5 ${invoiceBusy ? "animate-spin" : ""}`} /> Regenerate
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => { setDetail(null); openStatus(detail); }} className="px-4 py-2 text-xs uppercase border border-gray-300 hover:border-pink hover:text-pink transition-colors flex items-center gap-1.5" data-testid="order-detail-update-status">
                <Pencil className="w-3.5 h-3.5" /> Update Status
              </button>
              <button onClick={() => setDetail(null)} className={btnPink}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function CookieConsents({ api }) {
  const [data, setData] = useState({ total: 0, accepted: 0, rejected: 0, recent: [] });
  useEffect(() => {
    api().get("/admin/cookie-consents").then(({ data }) => setData(data)).catch(() => toast.error("Failed to load consents"));
  }, [api]);

  const Stat = ({ label, value, cls }) => (
    <div className={`bg-white border border-gray-100 p-5 flex-1 min-w-[140px] ${cls || ""}`}>
      <div className="text-3xl font-semibold text-charcoal">{value}</div>
      <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">{label}</div>
    </div>
  );

  return (
    <div data-testid="admin-cookies">
      <h2 className="font-serif text-3xl mb-6">Cookie Consents <span className="text-gray-400 text-lg">({data.total})</span></h2>
      <div className="flex flex-wrap gap-3 mb-6">
        <Stat label="Total recorded" value={data.total} />
        <Stat label="Accepted" value={data.accepted} />
        <Stat label="Rejected" value={data.rejected} />
      </div>
      <div className="bg-white border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-surface text-left text-xs uppercase tracking-wide text-gray-500"><th className="px-4 py-3">Choice</th><th className="px-4 py-3">Analytics</th><th className="px-4 py-3">Marketing</th><th className="px-4 py-3">Device</th><th className="px-4 py-3">Date</th></tr></thead>
          <tbody>
            {data.recent.map((c, i) => (
              <tr key={i} className="border-t border-gray-100" data-testid={`consent-row-${i}`}>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border rounded-full ${c.choice === "accepted" ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                    {c.choice === "accepted" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />} {c.choice}
                  </span>
                </td>
                <td className="px-4 py-3">{c.analytics ? "Yes" : "No"}</td>
                <td className="px-4 py-3">{c.marketing ? "Yes" : "No"}</td>
                <td className="px-4 py-3 text-gray-500 text-xs truncate max-w-[160px]">{c.device_id || "-"}</td>
                <td className="px-4 py-3 text-gray-500">{(c.created_at || "").slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
            {data.recent.length === 0 && <tr><td className="px-4 py-6 text-gray-400" colSpan={5}>No consents recorded yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- Settings ----------------
function SettingsTab({ api }) {
  const [form, setForm] = useState(null);
  useEffect(() => { api().get("/settings").then(({ data }) => setForm({
    popup_enabled: data.popup_enabled ?? true, popup_delay_seconds: data.popup_delay_seconds ?? 3,
    popup_headline: data.popup_headline || "", popup_subtext: data.popup_subtext || "",
    popup_discount_percent: data.popup_discount_percent ?? 15, popup_code: data.popup_code || "PKZ",
    popup_eyebrow: data.popup_eyebrow ?? "First Timer?",
    popup_button_text: data.popup_button_text ?? "Subscribe",
    popup_decline_text: data.popup_decline_text ?? "No, thanks",
    popup_success_title: data.popup_success_title ?? "You're in!",
    popup_success_subtext: data.popup_success_subtext ?? "Here's your code",
    sale_banner_text: data.sale_banner_text ?? "",
    announcement_text: data.announcement_text ?? "",
  })); }, [api]);
  if (!form) return null;
  const save = async () => {
    try {
      await api().put("/admin/settings", { ...form, popup_delay_seconds: parseInt(form.popup_delay_seconds) || 3, popup_discount_percent: parseFloat(form.popup_discount_percent) || 0 });
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
  };
  return (
    <div data-testid="admin-settings" className="max-w-lg">
      <h2 className="font-serif text-3xl mb-6">Popup & Welcome Offer</h2>
      <div className="bg-white border border-gray-100 p-6 space-y-4">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.popup_enabled} onChange={(e) => setForm({ ...form, popup_enabled: e.target.checked })} data-testid="settings-enabled" /> Show welcome popup</label>
        <Field label="Eyebrow (small text above the discount)"><input className={inputCls} value={form.popup_eyebrow} onChange={(e) => setForm({ ...form, popup_eyebrow: e.target.value })} data-testid="settings-eyebrow" /></Field>
        <Field label="Headline"><input className={inputCls} value={form.popup_headline} onChange={(e) => setForm({ ...form, popup_headline: e.target.value })} data-testid="settings-headline" /></Field>
        <Field label="Subtext"><textarea rows={2} className={inputCls} value={form.popup_subtext} onChange={(e) => setForm({ ...form, popup_subtext: e.target.value })} data-testid="settings-subtext" /></Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Subscribe button text"><input className={inputCls} value={form.popup_button_text} onChange={(e) => setForm({ ...form, popup_button_text: e.target.value })} data-testid="settings-button-text" /></Field>
          <Field label="Decline link text"><input className={inputCls} value={form.popup_decline_text} onChange={(e) => setForm({ ...form, popup_decline_text: e.target.value })} data-testid="settings-decline-text" /></Field>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Delay (s)"><input type="number" className={inputCls} value={form.popup_delay_seconds} onChange={(e) => setForm({ ...form, popup_delay_seconds: e.target.value })} /></Field>
          <Field label="Discount %"><input type="number" className={inputCls} value={form.popup_discount_percent} onChange={(e) => setForm({ ...form, popup_discount_percent: e.target.value })} data-testid="settings-percent" /></Field>
          <Field label="Code prefix"><input className={inputCls + " uppercase"} value={form.popup_code} onChange={(e) => setForm({ ...form, popup_code: e.target.value })} data-testid="settings-code" /></Field>
        </div>
        <p className="text-xs text-gray-400 -mt-1">Each subscriber gets their own unique code using this prefix, e.g. <span className="font-mono">{(form.popup_code || "PKZ")}-A4K9Q2</span>.</p>
        <div className="border-t border-gray-100 pt-4 mt-1">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-3">Success screen (after subscribing)</p>
          <Field label="Success title (large)"><input className={inputCls} value={form.popup_success_title} onChange={(e) => setForm({ ...form, popup_success_title: e.target.value })} data-testid="settings-success-title" /></Field>
          <div className="mt-4"><Field label="Success subtitle (above the code)"><input className={inputCls} value={form.popup_success_subtext} onChange={(e) => setForm({ ...form, popup_success_subtext: e.target.value })} data-testid="settings-success-subtext" /></Field></div>
          <p className="text-xs text-gray-400 mt-2">Note: the line "Create a free account to redeem this code at checkout." is always shown and cannot be edited.</p>
        </div>
        <button onClick={save} className={btnPink} data-testid="settings-save">Save Settings</button>
      </div>

      <h2 className="font-serif text-3xl mt-10 mb-2">Banners</h2>
      <p className="text-sm text-gray-500 mb-6">Optional messages at the top of the website. Leave a box empty to hide it.</p>
      <div className="bg-white border border-gray-100 p-6 space-y-5">
        <Field label="Green top bar">
          <input className={inputCls} value={form.announcement_text}
            onChange={(e) => setForm({ ...form, announcement_text: e.target.value })}
            data-testid="settings-announcement-text" placeholder="e.g. Eid Sale - up to 30% off" />
        </Field>
        <Field label="Gold scrolling strip">
          <input className={inputCls} value={form.sale_banner_text}
            onChange={(e) => setForm({ ...form, sale_banner_text: e.target.value })}
            data-testid="settings-sale-banner" placeholder="e.g. Eid Sale - up to 30% off" />
        </Field>
        <div className="text-[11px] uppercase tracking-[0.15em] rounded overflow-hidden border border-gray-100" data-testid="settings-banner-preview">
          <div className="bg-berry text-white text-center px-3 py-2">
            {form.announcement_text.trim() ? `${form.announcement_text.trim()}  ·  ` : ""}Free UK delivery on orders over £40
          </div>
          <div className="metallic-gold text-black text-center px-3 py-2 truncate">
            Fast Delivery | Free UK Delivery on Orders Over £40 | {form.sale_banner_text.trim() ? `${form.sale_banner_text.trim()} | ` : ""}Premium Quality
          </div>
        </div>
        <p className="text-xs text-gray-400 -mt-2">Preview of how the two bars will look.</p>
        <button onClick={save} className={btnPink} data-testid="settings-save-banner">Save Banners</button>
      </div>
    </div>
  );
}

function SaleTab({ api }) {
  const [form, setForm] = useState(null);
  useEffect(() => { api().get("/settings").then(({ data }) => setForm(data)); }, [api]);
  if (!form) return null;

  const save = async () => {
    try {
      await api().put("/admin/settings", {
        ...form,
        popup_delay_seconds: parseInt(form.popup_delay_seconds) || 3,
        popup_discount_percent: parseFloat(form.popup_discount_percent) || 0,
      });
      toast.success("Sale countdown saved");
    } catch { toast.error("Save failed"); }
  };

  const enabled = !!form.sale_countdown_enabled;
  const endVal = (form.sale_countdown_end || "").slice(0, 16); // datetime-local wants YYYY-MM-DDTHH:mm
  const endMs = form.sale_countdown_end ? new Date(form.sale_countdown_end).getTime() : NaN;
  const live = enabled && !Number.isNaN(endMs) && endMs > Date.now();

  return (
    <div data-testid="admin-sale" className="max-w-lg">
      <h2 className="font-serif text-3xl mb-2">Sale Countdown</h2>
      <p className="text-sm text-gray-500 mb-6">A slim countdown strip shows under the top announcement bar. It disappears automatically once the end date passes.</p>
      <div className="bg-white border border-gray-100 p-6 space-y-5">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} data-testid="sale-enabled"
            onChange={(e) => setForm({ ...form, sale_countdown_enabled: e.target.checked })} />
          Show the sale countdown strip
        </label>
        <Field label="Label (shown before the timer)">
          <input className={inputCls} value={form.sale_countdown_label || ""} data-testid="sale-label"
            onChange={(e) => setForm({ ...form, sale_countdown_label: e.target.value })}
            placeholder="e.g. Summer Sale ends in" />
        </Field>
        <Field label="Sale end date & time (UK time)">
          <input type="datetime-local" className={inputCls} value={endVal} data-testid="sale-end"
            onChange={(e) => setForm({ ...form, sale_countdown_end: e.target.value })} />
        </Field>
        <div className={`text-xs px-3 py-2 rounded ${live ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`} data-testid="sale-status">
          {enabled
            ? (Number.isNaN(endMs) ? "Set an end date to activate the countdown."
              : endMs > Date.now() ? "Countdown is LIVE and showing on the site." : "End date has already passed — the strip is hidden.")
            : "Countdown is switched off."}
        </div>
        <button onClick={save} className={btnPink} data-testid="sale-save">Save Countdown</button>
      </div>
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("products");
  const [navOpen, setNavOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = () => { logout(); navigate("/admin/login"); };
  const api = adminApi;
  const activeLabel = (TABS.find((t) => t.id === tab) || {}).label || "";

  const NavLinks = () => (
    <>
      <nav className="flex-1 py-4">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => { setTab(t.id); setNavOpen(false); }} data-testid={`tab-${t.id}`}
            className={`w-full flex items-center gap-3 px-6 py-3 text-sm transition-colors ${tab === t.id ? "bg-pink text-white" : "text-white/70 hover:text-white hover:bg-white/5"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </nav>
      <a href="/" target="_blank" rel="noopener noreferrer" data-testid="admin-view-website"
        className="flex items-center gap-3 px-6 py-4 text-sm text-white/70 hover:text-white border-t border-white/10">
        <ExternalLink className="w-4 h-4" /> View Website
      </a>
      <button onClick={onLogout} data-testid="admin-logout" className="flex items-center gap-3 px-6 py-4 text-sm text-white/70 hover:text-white border-t border-white/10">
        <LogOut className="w-4 h-4" /> Logout
      </button>
    </>
  );

  return (
    <div className="min-h-screen bg-surface lg:flex" data-testid="admin-dashboard">
      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 bg-charcoal text-white flex items-center justify-between px-4 py-3">
        <button onClick={() => setNavOpen(true)} data-testid="admin-nav-toggle" aria-label="Open menu" className="p-1">
          <Menu className="w-6 h-6" />
        </button>
        <span className="font-serif text-sm uppercase tracking-[0.2em] text-white/90">{activeLabel}</span>
        <img src={LOGO_WHITE_URL} alt="Zebaa" className="h-5 w-auto object-contain" />
      </header>

      {/* Mobile drawer overlay */}
      {navOpen && (
        <div className="lg:hidden fixed inset-0 z-[90]" data-testid="admin-nav-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setNavOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 max-w-[80%] bg-charcoal text-white flex flex-col animate-fade-in">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <div>
                <img src={LOGO_WHITE_URL} alt="Zebaa" className="h-7 w-auto object-contain" />
                <div className="font-serif text-[10px] uppercase tracking-[0.4em] text-white/60 mt-2.5">Admin Panel</div>
              </div>
              <button onClick={() => setNavOpen(false)} aria-label="Close menu" className="text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <NavLinks />
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-60 bg-charcoal text-white flex-col shrink-0 min-h-screen sticky top-0">
        <div className="p-6 border-b border-white/10">
          <img src={LOGO_WHITE_URL} alt="Zebaa" className="h-7 w-auto object-contain" />
          <div className="font-serif text-[10px] uppercase tracking-[0.4em] text-white/60 mt-2.5">Admin Panel</div>
        </div>
        <NavLinks />
      </aside>

      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-10 overflow-x-hidden">
        {tab === "products" && <Products api={api} />}
        {tab === "offers" && <Offers api={api} />}
        {tab === "sale" && <SaleTab api={api} />}
        {tab === "discounts" && <Discounts api={api} />}
        {tab === "customers" && <Customers api={api} />}
        {tab === "subscribers" && <Subscribers api={api} />}
        {tab === "orders" && <Orders api={api} />}
        {tab === "cookies" && <CookieConsents api={api} />}
        {tab === "settings" && <SettingsTab api={api} />}
      </main>
    </div>
  );
}
